'use client'

interface DocumentViewerProps {
  src: string
  title: string
  name: string
}

const getFileType = (url: string): string => {
  const extension = url.split('.').pop()?.toLowerCase()
  return extension || ''
}

const canDisplayInIframe = (fileType: string): boolean => {
  return ['html', 'htm'].includes(fileType)
}

const canUseMicrosoftViewer = (fileType: string): boolean => {
  return ['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileType)
}

const canUsePDFJs = (fileType: string): boolean => {
  return fileType === 'pdf'
}

const getMicrosoftViewerUrl = (src: string): string => {
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(src)}`
}

const getPDFJsViewerUrl = (src: string): string => {
  return `https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(src)}`
}

const renderDocumentLink = (src: string, children: React.ReactNode) => {
  return (
    <a href={src} target='_blank' rel='noopener noreferrer' className='underline ml-1'>
      {children}
    </a>
  )
}

const renderIframeInfoMessage = (src: string) => {
  return (
    <>
      This document is displayed in an embedded viewer. If you have trouble viewing it, you can
      {renderDocumentLink(src, 'open it in a new tab')}.
    </>
  )
}

const renderViewerInfoMessage = (src: string) => {
  let viewerName = 'embedded viewer'

  return (
    <>
      This document is displayed using {viewerName}. If you have trouble viewing it, you can
      {renderDocumentLink(src, 'download or open it in a new tab')}.
    </>
  )
}

const renderDownloadInfoMessage = (src: string) => {
  return (
    <>
      This document cannot be displayed inline. Please
      {renderDocumentLink(src, 'download or open it in a new tab')} to view the content.
    </>
  )
}

const getInfoMessage = (
  src: string,
  isIframeSupported: boolean,
  canUseMicrosoftViewer: boolean,
  canUsePDFJs: boolean
) => {
  if (isIframeSupported) {
    return renderIframeInfoMessage(src)
  } else if (canUseMicrosoftViewer || canUsePDFJs) {
    return renderViewerInfoMessage(src)
  } else {
    return renderDownloadInfoMessage(src)
  }
}

const renderDocumentTitle = (name: string) => {
  return (
    <p className='text-sm text-[#2CA0D2]'>
      <strong>Document:</strong> {name}
    </p>
  )
}

const renderInfoMessage = (
  src: string,
  isIframeSupported: boolean,
  canUseMicrosoftViewer: boolean,
  canUsePDFJs: boolean
) => {
  return (
    <p className='text-xs text-[#2CA0D2] mt-1'>
      {getInfoMessage(src, isIframeSupported, canUseMicrosoftViewer, canUsePDFJs)}
    </p>
  )
}

const renderDocumentInfo = (
  name: string,
  src: string,
  isIframeSupported: boolean,
  canUseMicrosoftViewer: boolean,
  canUsePDFJs: boolean
) => {
  return (
    <div className='mb-4 p-3 bg-[#2CA0D2]/5 border border-[#2CA0D2]/30 rounded-lg'>
      {renderDocumentTitle(name)}
      {renderInfoMessage(src, isIframeSupported, canUseMicrosoftViewer, canUsePDFJs)}
    </div>
  )
}

const renderIframe = (src: string, title: string) => {
  return (
    <iframe
      src={src}
      className='w-full h-full border border-gray-200 rounded-lg shadow-sm'
      title={title}
      style={{}}
      loading='lazy'
    />
  )
}

const renderMicrosoftViewer = (src: string, title: string) => {
  const microsoftViewerUrl = getMicrosoftViewerUrl(src)
  return (
    <div className='mobile-zoom-container border border-gray-200 rounded-lg shadow-sm overflow-hidden'>
      <iframe
        src={microsoftViewerUrl}
        className='w-full h-full rounded-lg shadow-sm'
        title={title}
        style={{ border: 'none', margin: '-1px' }}
      />
    </div>
  )
}

const renderPDFJsViewer = (src: string, title: string) => {
  const pdfJsUrl = getPDFJsViewerUrl(src)
  return (
    <iframe
      src={pdfJsUrl}
      className='w-full h-full border border-gray-200 rounded-lg shadow-sm'
      title={title}
      style={{}}
    />
  )
}

const renderDownloadIcon = () => {
  return (
    <svg className='w-4 h-4 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
      />
    </svg>
  )
}

const renderDownloadInterface = (src: string, fileType: string) => {
  return (
    <div className='w-full h-[400px] border border-gray-200 rounded-lg shadow-sm flex flex-col items-center justify-center bg-gray-50'>
      <div className='text-center p-8'>
        <div className='text-6xl mb-4'>📄</div>
        <h3 className='text-lg font-semibold text-gray-700 mb-2'>Document Preview Not Available</h3>
        <p className='text-gray-500 mb-4'>This file type ({fileType.toUpperCase()}) cannot be displayed inline.</p>
        <a
          href={src}
          target='_blank'
          rel='noopener noreferrer'
          className='inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors'
        >
          {renderDownloadIcon()}
          Download Document
        </a>
      </div>
    </div>
  )
}

const renderDocumentContent = (
  src: string,
  title: string,
  fileType: string,
  isIframeSupported: boolean,
  canUseMicrosoftViewer: boolean,
  canUsePDFJs: boolean
) => {
  if (isIframeSupported) {
    return renderIframe(src, title)
  } else if (canUsePDFJs) {
    return renderPDFJsViewer(src, title)
  } else if (canUseMicrosoftViewer) {
    return renderMicrosoftViewer(src, title)
  } else {
    return renderDownloadInterface(src, fileType)
  }
}

export default function DocumentViewer({ src, title, name }: DocumentViewerProps) {
  const fileType = getFileType(src)
  const isIframeSupported = canDisplayInIframe(fileType)
  const supportsMicrosoftViewer = canUseMicrosoftViewer(fileType)
  const supportsPDFJs = canUsePDFJs(fileType)

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @media (max-width: 768px) {
            .mobile-zoom-container {
              zoom: 0.5;
              height: 100%;
              width: 100%;
            }
          }
        `
        }}
      />
      <div className='h-full w-full'>
        {renderDocumentContent(src, title, fileType, isIframeSupported, supportsMicrosoftViewer, supportsPDFJs)}
      </div>
    </>
  )
}
